import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { PlayArrow as RunIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { reversalCatcherAPI } from '../services/api';
import {
  formatExitReason,
  formatSignalState,
  formatVndAmount,
  getReversalCatcherLocale,
  parseVndAmount,
} from '../components/reversal-catcher/reversalCatcherFormatters';

const COLORS = ['#64748b', '#059669', '#dc2626', '#d97706', '#6366f1'];

const panelSx = {
  border: '1px solid #e5e7eb',
  borderRadius: 1,
  bgcolor: '#fff',
  overflow: 'hidden',
  mb: 1.25,
};

const sectionTitleSx = {
  fontSize: '0.62rem',
  fontWeight: 600,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  px: 1.25,
  py: 0.55,
  borderBottom: '1px solid #eef2f7',
  bgcolor: '#fafbfc',
};

const denseFieldSx = {
  '& .MuiInputBase-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
};

/** Widths tuned to content — avoids equal columns truncating selects or over-sizing numbers */
const configFieldWidth = {
  date: 148,
  capital: 132,
  shortNum: 76,
  snapMode: 208,
  signalMode: 168,
};

const selectSx = {
  ...denseFieldSx,
  '& .MuiSelect-select': { fontSize: '0.78rem' },
};

function pnlColor(v) {
  if (v > 0) return '#059669';
  if (v < 0) return '#dc2626';
  return '#64748b';
}

function Panel({ title, children, action }) {
  return (
    <Box sx={panelSx}>
      <Box sx={{ ...sectionTitleSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{title}</span>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function MetricCell({ label, value, sub, valueColor = '#0f172a' }) {
  return (
    <Box sx={{
      flex: '1 1 0',
      minWidth: 72,
      px: 1.25,
      py: 0.65,
      borderRight: '1px solid #eef2f7',
      '&:last-of-type': { borderRight: 'none' },
    }}>
      <Typography sx={{
        fontSize: '0.58rem',
        fontWeight: 600,
        color: '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        mb: 0.2,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: '0.88rem',
        fontWeight: 700,
        color: valueColor,
        fontFamily: 'ui-monospace, monospace',
        lineHeight: 1.2,
      }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mt: 0.1 }}>{sub}</Typography>
      )}
    </Box>
  );
}

const HISTORY_COLS = [
  { id: 'runDate', min: 92, fr: 0.95, align: 'left' },
  { id: 'period', min: 148, fr: 1.05, align: 'left' },
  { id: 'trades', min: 48, fr: 0.6, align: 'right' },
  { id: 'winRate', min: 56, fr: 0.65, align: 'right' },
  { id: 'return', min: 64, fr: 0.7, align: 'right' },
  { id: 'sharpe', min: 64, fr: 0.75, align: 'right', padEnd: 4 },
  { id: 'mode', min: 140, fr: 1.15, align: 'left', padStart: 3 },
  { id: 'load', min: 48, fr: 0.55, align: 'center' },
];

const historyGridTemplate = HISTORY_COLS
  .map((c) => `minmax(${c.min}px, ${c.fr}fr)`)
  .join(' ');

function BacktestHistoryTable({ history, t, dateLocale, onLoad }) {
  const headers = [
    t('reversalCatcher.backtest.history.runDate'),
    t('reversalCatcher.backtest.history.period'),
    t('reversalCatcher.backtest.history.trades'),
    t('reversalCatcher.backtest.history.winRate'),
    t('reversalCatcher.backtest.history.return'),
    t('reversalCatcher.backtest.history.sharpe'),
    t('reversalCatcher.backtest.history.mode'),
    t('reversalCatcher.backtest.history.load'),
  ];

  const historyGridSx = {
    display: 'grid',
    gridTemplateColumns: historyGridTemplate,
    columnGap: 2.5,
    width: '100%',
    minWidth: 760,
    px: 1.25,
    alignItems: 'center',
  };

  const historyTableWrapSx = {
    pb: 1.25,
  };

  const cellSx = (col) => ({
    minWidth: 0,
    textAlign: col.align,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...(col.padEnd != null ? { pr: col.padEnd } : {}),
    ...(col.padStart != null ? { pl: col.padStart } : {}),
  });

  return (
    <Box sx={{ overflowX: 'auto', ...historyTableWrapSx }}>
      <Box sx={{
        ...historyGridSx,
        py: 0.5,
        bgcolor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
      }}>
        {headers.map((h, i) => (
          <Typography key={HISTORY_COLS[i].id} sx={{
            ...cellSx(HISTORY_COLS[i]),
            fontSize: '0.62rem',
            fontWeight: 700,
            color: '#64748b',
            textTransform: 'uppercase',
          }}>
            {h}
          </Typography>
        ))}
      </Box>
      {history.map((run, idx) => {
        const winRate = (run.win_rate || 0) * 100;
        const retPct = ((run.total_return || 0) / (run.initial_capital || 100000000)) * 100;
        const modeLabel = run.snap_mode === 'aggressive'
          ? t('reversalCatcher.filters.aggressive')
          : run.snap_mode === 'conservative'
            ? t('reversalCatcher.filters.conservative')
            : t('common.na');
        return (
          <Box
            key={run.id}
            sx={{
              ...historyGridSx,
              py: 0.45,
              borderBottom: '1px solid #f1f5f9',
              bgcolor: idx % 2 === 1 ? '#fafbfc' : '#fff',
              '&:hover': { bgcolor: '#f8fafc' },
            }}
          >
            <Typography sx={{ ...cellSx(HISTORY_COLS[0]), fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace' }}>
              {run.run_date
                ? new Date(run.run_date).toLocaleString(dateLocale, {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })
                : t('common.na')}
            </Typography>
            <Typography sx={{ ...cellSx(HISTORY_COLS[1]), fontSize: '0.68rem', color: '#475569' }}>
              {run.start_date} → {run.end_date}
            </Typography>
            <Typography sx={{ ...cellSx(HISTORY_COLS[2]), fontSize: '0.72rem', fontWeight: 600, fontFamily: 'monospace', color: '#1e293b' }}>
              {run.total_trades || 0}
            </Typography>
            <Typography sx={{ ...cellSx(HISTORY_COLS[3]), fontSize: '0.72rem', fontWeight: 700, color: pnlColor(winRate - 50), fontFamily: 'monospace' }}>
              {winRate.toFixed(1)}%
            </Typography>
            <Typography sx={{ ...cellSx(HISTORY_COLS[4]), fontSize: '0.72rem', fontWeight: 700, color: pnlColor(retPct), fontFamily: 'monospace' }}>
              {retPct >= 0 ? '+' : ''}{retPct.toFixed(2)}%
            </Typography>
            <Typography sx={{ ...cellSx(HISTORY_COLS[5]), fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace' }}>
              {run.sharpe_ratio != null ? run.sharpe_ratio.toFixed(2) : '—'}
            </Typography>
            <Typography sx={{
              ...cellSx(HISTORY_COLS[6]),
              fontSize: '0.65rem',
              fontWeight: 600,
              color: run.snap_mode === 'aggressive' ? '#d97706' : '#2563eb',
            }}>
              {modeLabel}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                size="small"
                variant="text"
                onClick={() => onLoad(run.id)}
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  py: 0.15,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  color: '#2563eb',
                  textTransform: 'none',
                }}
              >
                {t('reversalCatcher.backtest.history.load')}
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

const ReversalCatcherBacktest = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = getReversalCatcherLocale(i18n.language);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [, setLoadingHistory] = useState(false);
  const [config, setConfig] = useState({
    start_date: '2024-01-01',
    end_date: '2024-12-31',
    initial_capital: 100000000,
    max_positions: 5,
    snap_mode: 'conservative',
    min_c_score: 2,
    only_capitulation: false,
    use_settlement: true,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await reversalCatcherAPI.getBacktestHistory();
      setHistory(response.data.runs || []);
    } catch (error) {
      console.error('Error fetching backtest history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  React.useEffect(() => {
    fetchHistory();
  }, []);

  const handleConfigChange = (field, value) => {
    setConfig({ ...config, [field]: value });
  };

  const handleLoadBacktest = async (runId) => {
    try {
      const response = await reversalCatcherAPI.getBacktestRun(runId);
      setResults(response.data);
      setSnackbar({ open: true, message: t('reversalCatcher.messages.dataLoaded'), severity: 'success' });
    } catch (error) {
      console.error('Error loading backtest:', error);
      setSnackbar({
        open: true,
        message: t('reversalCatcher.messages.dataLoadFailed', {
          error: error.response?.data?.detail || error.message || t('reversalCatcher.errors.unknown'),
        }),
        severity: 'error',
      });
    }
  };

  const handleRunBacktest = async () => {
    setLoading(true);
    setSnackbar({ open: true, message: t('reversalCatcher.messages.backtestStarted'), severity: 'info' });
    try {
      const response = await reversalCatcherAPI.runBacktest(config);
      setResults(response.data);
      fetchHistory();
      setSnackbar({ open: true, message: t('reversalCatcher.messages.backtestComplete'), severity: 'success' });
    } catch (error) {
      console.error('Backtest error:', error);
      setSnackbar({
        open: true,
        message: t('reversalCatcher.messages.backtestFailed', {
          error: error.response?.data?.detail || error.message || t('reversalCatcher.errors.unknown'),
        }),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const exitBreakdown = results?.exit_breakdown
    ? Object.entries(results.exit_breakdown).map(([key, value]) => ({
      name: formatExitReason(t, key),
      value,
    }))
    : [];

  const retPct = (results?.total_return_pct || 0) * 100;

  return (
    <Box>
      {/* Config — single compact grid */}
      <Panel title={t('reversalCatcher.backtest.title')}>
        <Box sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: 1,
          px: 1.25,
          pt: 1.25,
          pb: 1.75,
          alignItems: 'flex-end',
          overflowX: 'auto',
        }}>
          <TextField
            size="small"
            type="date"
            label={t('reversalCatcher.backtest.startDate')}
            value={config.start_date}
            onChange={(e) => handleConfigChange('start_date', e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ ...denseFieldSx, width: configFieldWidth.date, flex: '0 0 auto', flexShrink: 0 }}
          />
          <TextField
            size="small"
            type="date"
            label={t('reversalCatcher.backtest.endDate')}
            value={config.end_date}
            onChange={(e) => handleConfigChange('end_date', e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ ...denseFieldSx, width: configFieldWidth.date, flex: '0 0 auto', flexShrink: 0 }}
          />
          <TextField
            size="small"
            type="text"
            inputMode="numeric"
            label={t('reversalCatcher.backtest.initialCapital')}
            value={formatVndAmount(config.initial_capital, i18n.language)}
            onChange={(e) => handleConfigChange('initial_capital', parseVndAmount(e.target.value))}
            slotProps={{
              htmlInput: {
                sx: { fontFamily: 'ui-monospace, monospace', textAlign: 'right' },
              },
            }}
            sx={{ ...denseFieldSx, width: configFieldWidth.capital, flex: '0 0 auto', flexShrink: 0 }}
          />
          <TextField
            size="small"
            type="number"
            label={t('reversalCatcher.backtest.maxPositionsShort')}
            value={config.max_positions}
            onChange={(e) => handleConfigChange('max_positions', parseInt(e.target.value, 10) || 1)}
            slotProps={{ htmlInput: { min: 1, max: 10 } }}
            sx={{ ...denseFieldSx, width: configFieldWidth.shortNum, flex: '0 0 auto', flexShrink: 0 }}
          />
          <FormControl size="small" sx={{ ...selectSx, width: configFieldWidth.snapMode, flex: '0 0 auto', flexShrink: 0 }}>
            <InputLabel>{t('reversalCatcher.backtest.snapMode')}</InputLabel>
            <Select
              value={config.snap_mode}
              label={t('reversalCatcher.backtest.snapMode')}
              onChange={(e) => handleConfigChange('snap_mode', e.target.value)}
            >
              <MenuItem value="aggressive">{t('reversalCatcher.filters.aggressive')}</MenuItem>
              <MenuItem value="conservative">{t('reversalCatcher.filters.conservative')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label={t('reversalCatcher.backtest.minCScoreShort')}
            value={config.min_c_score}
            onChange={(e) => handleConfigChange('min_c_score', parseInt(e.target.value, 10) || 0)}
            slotProps={{ htmlInput: { min: 0, max: 3 } }}
            sx={{ ...denseFieldSx, width: configFieldWidth.shortNum, flex: '0 0 auto', flexShrink: 0 }}
          />
          <FormControl size="small" sx={{ ...selectSx, width: configFieldWidth.signalMode, flex: '0 0 auto', flexShrink: 0 }}>
            <InputLabel>{t('reversalCatcher.backtest.mode')}</InputLabel>
            <Select
              value={config.only_capitulation}
              label={t('reversalCatcher.backtest.mode')}
              onChange={(e) => handleConfigChange('only_capitulation', e.target.value)}
            >
              <MenuItem value={false}>{t('reversalCatcher.backtest.allSignals')}</MenuItem>
              <MenuItem value>{t('reversalCatcher.backtest.capitulationOnly')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RunIcon sx={{ fontSize: 16 }} />}
            onClick={handleRunBacktest}
            sx={{
              height: 40,
              flex: '0 0 auto',
              flexShrink: 0,
              ml: 0.5,
              fontWeight: 600,
              fontSize: '0.76rem',
              textTransform: 'none',
              borderColor: '#e2e8f0',
              color: '#475569',
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: '#6366f1', color: '#6366f1', bgcolor: 'rgba(99,102,241,0.04)' },
            }}
          >
            {loading ? t('reversalCatcher.backtest.running') : t('reversalCatcher.backtest.run')}
          </Button>
        </Box>
      </Panel>

      {/* History */}
      {history.length > 0 && (
        <Panel
          title={`${t('reversalCatcher.backtest.history.title')} (${history.length})`}
        >
          <BacktestHistoryTable
            history={history}
            t={t}
            dateLocale={dateLocale}
            onLoad={handleLoadBacktest}
          />
        </Panel>
      )}

      {/* Results */}
      {results && (
        <>
          <Panel title={t('reversalCatcher.backtest.results.summary')}>
            <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              <MetricCell
                label={t('reversalCatcher.backtest.results.totalTrades')}
                value={String(results.total_trades ?? 0)}
                sub={`${t('reversalCatcher.backtest.results.win')}: ${results.winning_trades ?? 0} · ${t('reversalCatcher.backtest.results.loss')}: ${results.losing_trades ?? 0}`}
              />
              <MetricCell
                label={t('reversalCatcher.backtest.results.winRate')}
                value={`${((results.win_rate || 0) * 100).toFixed(1)}%`}
                valueColor={pnlColor((results.win_rate || 0) - 0.5)}
              />
              <MetricCell
                label={t('reversalCatcher.backtest.results.totalReturn')}
                value={`${retPct >= 0 ? '+' : ''}${retPct.toFixed(2)}%`}
                valueColor={pnlColor(retPct)}
                sub={`${(results.total_return || 0).toLocaleString(dateLocale)} VND`}
              />
              <MetricCell
                label={t('reversalCatcher.backtest.results.maxDrawdown')}
                value={`${((results.max_drawdown_pct || 0) * 100).toFixed(2)}%`}
                valueColor="#dc2626"
              />
              <MetricCell
                label={t('reversalCatcher.backtest.results.finalCapital')}
                value={(results.final_capital || 0).toLocaleString(dateLocale)}
              />
              <MetricCell
                label={t('reversalCatcher.backtest.results.sharpeRatio')}
                value={(results.sharpe_ratio || 0).toFixed(2)}
              />
              <MetricCell
                label={t('reversalCatcher.backtest.results.profitFactor')}
                value={(results.profit_factor || 0).toFixed(2)}
              />
            </Box>
          </Panel>

          {(exitBreakdown.length > 0 || (results.equity_curve?.length > 0)) && (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: exitBreakdown.length > 0 && results.equity_curve?.length > 0 ? '280px 1fr' : '1fr' },
              gap: 1.25,
              mb: 1.25,
            }}>
              {exitBreakdown.length > 0 && (
                <Panel title={t('reversalCatcher.backtest.results.exitBreakdown')}>
                  <Box sx={{ px: 0.5, py: 0.5 }}>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={exitBreakdown}
                          cx="50%"
                          cy="50%"
                          innerRadius={44}
                          outerRadius={72}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {exitBreakdown.map((entry, index) => (
                            <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Panel>
              )}
              {results.equity_curve?.length > 0 && (
                <Panel title={t('reversalCatcher.backtest.results.equityCurve')}>
                  <Box sx={{ px: 0.5, py: 0.5 }}>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={results.equity_curve}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} width={56} />
                        <RechartsTooltip formatter={(value) => `${Number(value).toLocaleString(dateLocale)} VND`} />
                        <Line type="monotone" dataKey="equity" stroke="#6366f1" strokeWidth={1.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Panel>
              )}
            </Box>
          )}

          {results.trades?.length > 0 && (
            <Panel title={t('reversalCatcher.backtest.results.tradeHistory', { count: results.trades.length })}>
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '0.7fr 0.85fr 0.85fr 0.9fr 0.9fr 0.65fr 0.55fr 1fr 0.8fr',
                  minWidth: 780,
                  px: 1,
                  py: 0.45,
                  bgcolor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                }}>
                  {[
                    t('reversalCatcher.backtest.results.ticker'),
                    t('reversalCatcher.backtest.results.entryDate'),
                    t('reversalCatcher.backtest.results.exitDate'),
                    t('reversalCatcher.backtest.results.entryPrice'),
                    t('reversalCatcher.backtest.results.exitPrice'),
                    t('reversalCatcher.backtest.results.return'),
                    t('reversalCatcher.backtest.results.rMultiple'),
                    t('reversalCatcher.backtest.results.exitReason'),
                    t('common.state'),
                  ].map((h, i) => (
                    <Typography key={h} sx={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      textAlign: i >= 3 && i <= 6 ? 'right' : 'left',
                    }}>
                      {h}
                    </Typography>
                  ))}
                </Box>
                {results.trades.map((trade, idx) => (
                  <Box
                    key={`${trade.ticker}-${idx}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '0.7fr 0.85fr 0.85fr 0.9fr 0.9fr 0.65fr 0.55fr 1fr 0.8fr',
                      minWidth: 780,
                      px: 1,
                      py: 0.35,
                      alignItems: 'center',
                      borderBottom: '1px solid #f8fafc',
                      bgcolor: idx % 2 === 1 ? '#fafbfc' : '#fff',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{trade.ticker}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace' }}>{trade.entry_date}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace' }}>{trade.exit_date || '—'}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', textAlign: 'right', fontFamily: 'monospace' }}>
                      {(trade.entry_price || 0).toLocaleString(dateLocale)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', textAlign: 'right', fontFamily: 'monospace' }}>
                      {trade.exit_price ? trade.exit_price.toLocaleString(dateLocale) : '—'}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      color: pnlColor(trade.pct_return),
                    }}>
                      {trade.pct_return >= 0 ? '+' : ''}{(trade.pct_return * 100).toFixed(2)}%
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', textAlign: 'right', fontFamily: 'monospace', color: '#64748b' }}>
                      {trade.r_multiple
                        ? t('reversalCatcher.backtest.results.rMultipleValue', { value: trade.r_multiple.toFixed(2) })
                        : '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#475569' }}>
                      {trade.exit_reason ? formatExitReason(t, trade.exit_reason) : t('reversalCatcher.backtest.results.open')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>
                      {formatSignalState(t, trade.state)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Panel>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReversalCatcherBacktest;
